import { useRecoilValue } from 'recoil';

import { CarrierLogoStack } from '@/components/modules/carrier/logo-stack';
import { CarrierNameList } from '@/components/modules/carrier/name-list';
import { EmissionsIndicator } from '@/components/modules/emissions/indicator';
import { JourneyRowTemplate } from '@/components/modules/journey/row-template';
import { JourneyTextBlock } from '@/components/modules/journey/text-block';
import { OptionBarSequence } from '@/components/modules/option/bar-sequence';
import { getOptionSequenceItems } from '@/components/modules/option/bar-sequence/utils';
import { OptionRowComponentProps, sharedOptionRowStyles } from '@/components/modules/option/row';
import { VehicleNameList } from '@/components/modules/vehicle/name-list';
import { VehicleTypeBadge } from '@/components/ui/vehicle-type-badge';
import { MODE_AIR } from '@/lib/constants';
import { locationsState } from '@/state/locations';
import { formatDuration, formatFrequency } from '@/utils/format';
import { getOptionModelStopsListString, getOptionModelStopsShortString } from '@/utils/strings';
import { tv } from '@/utils/styles';

const OptionRowAir = ({ option, emissionsStats, onClickDetails, maxDurationRatio }: OptionRowComponentProps) => {
  const locations = useRecoilValue(locationsState);
  const sequenceItems = getOptionSequenceItems({ option, locations });
  const { totalDurationSeconds, frequency, hasEndToEndFreighterAvailable, emissions } = option;
  const { base } = styles();
  const { bar, gridBlock } = sharedOptionRowStyles();

  return (
    <JourneyRowTemplate onClickDetails={onClickDetails}>
      <div className={base()}>
        <div className={gridBlock({ row: true, className: 'gap-4' })}>
          <CarrierLogoStack carrierIds={option.carrierIds} mode={MODE_AIR} variant="md" />
          <JourneyTextBlock
            primaryText={formatDuration(totalDurationSeconds)}
            subText={frequency ? formatFrequency(frequency) : ''}
          />
        </div>
        <div className={gridBlock()}>
          <JourneyTextBlock
            primaryText={getOptionModelStopsShortString({ option, mode: MODE_AIR })}
            subText={getOptionModelStopsListString({ option, mode: MODE_AIR, locations })}
          />
        </div>
        <div className={gridBlock({ withGap: true })}>
          <div className={bar()}>
            <OptionBarSequence sequenceItems={sequenceItems} ratio={maxDurationRatio} />
          </div>
          <div className="px-4">
            <CarrierNameList carrierIds={option.carrierIds} />
          </div>
        </div>
        <div className={gridBlock({ withGap: true })}>
          <div>
            <VehicleTypeBadge
              label={`Freighter ${hasEndToEndFreighterAvailable ? 'available' : 'N/A'}`}
              isActive={hasEndToEndFreighterAvailable}
            />
          </div>
          <VehicleNameList vehicleIds={option.vehicleIds || []} />
        </div>
        <div className={gridBlock({ withGap: true, center: true })}>
          <EmissionsIndicator
            mode={MODE_AIR}
            value={emissions?.average}
            unit={emissions?.unit}
            comparison={emissionsStats}
            wtwBreakdown={emissions?.wtw}
            averageMsg="Average for similar flights"
          />
        </div>
      </div>
    </JourneyRowTemplate>
  );
};

const styles = tv({
  slots: {
    base: 'relative grid grid-cols-[185px_130px_minmax(300px,2fr)_minmax(150px,1fr)_120px] leading-[14px]',
  },
});

export { OptionRowAir };
